import { useState, useCallback } from 'react';
import { searchContactsByName, searchContactsByNameInLead, ContactSearchResult } from '@/services/api/contacts';
import { fetchLeadWithContacts } from '@/services/api/leads';
import { useDebounceSearch } from './useDebounceSearch';

export const useContactSearch = () => {
  const [contacts, setContacts] = useState<ContactSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentLeadId, setCurrentLeadId] = useState<number | undefined>();

  const performSearch = useCallback(async (name: string) => {
    if (!name.trim()) {
      setContacts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 Searching contacts for:', name, currentLeadId ? `in lead ${currentLeadId}` : '');
      let response;
      if (currentLeadId) {
        response = await searchContactsByNameInLead(name, currentLeadId);
      } else {
        response = await searchContactsByName(name);
      }
      
      console.log('✅ Contact search results:', response.contacts || []);
      setContacts(response.contacts || []);
    } catch (error) {
      console.error('💥 Error searching contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [currentLeadId]);

  // New function to load contacts for a specific lead without search term
  const loadContactsForLead = useCallback(async (leadId: number) => {
    setCurrentLeadId(leadId);
    setLoading(true);
    try {
      console.log('🔍 Loading all contacts for lead:', leadId);
      // Use the consolidated endpoint
      const response = await fetchLeadWithContacts(leadId.toString());
      console.log('✅ Lead contacts loaded:', response.contacts || []);
      
      // Transform LeadContact[] to ContactSearchResult[] format
      const transformedContacts: ContactSearchResult[] = (response.contacts || []).map(contact => ({
        id: typeof contact.id === 'string' ? parseInt(contact.id) : contact.id,
        lead_id: leadId,
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: Array.isArray(contact.email) ? contact.email[0]?.email : contact.email,
        phone: contact.phone_number || (Array.isArray(contact.phone) ? contact.phone[0]?.phone : contact.phone)
      }));
      
      setContacts(transformedContacts);
    } catch (error) {
      console.error('💥 Error loading lead contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const { debouncedSearch } = useDebounceSearch(performSearch, { minLength: 3, delay: 300 });

  const searchContacts = useCallback((name: string, leadId?: number) => {
    setCurrentLeadId(leadId);
    debouncedSearch(name);
  }, [debouncedSearch]);

  const clearContacts = useCallback(() => {
    setContacts([]);
    setCurrentLeadId(undefined);
  }, []);

  return { contacts, loading, searchContacts, loadContactsForLead, clearContacts };
};